import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import ReactDom from 'react-dom';
import { useForm } from 'react-hook-form';

import { useStopwatch } from 'react-timer-hook';
import * as yup from 'yup';

import { Icon as BlueIcon } from '@blueprintjs/core';

import BackIconImage from '@assets/svg/back-icon.svg';
import FileIcon from '@assets/svg/file-document.svg';

import { TemplateDownloadDialog } from '@components';

import { useMutation } from 'react-query';

import { inventoryImport } from '@services/queries/onboarding/inventoryImport';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Back,
  Confirm,
  Row,
  Label,
  Line,
  Input,
  SelectFile,
  Form,
  Divider,
  SpacingDivider,
  ProgressContainer,
  FileImage,
  HolderDiv,
  TimerText,
  FileNameText,
  DescriptionText,
  TemplateDownloadSection,
  DownloadLink,
} from './ImportInventory.styles';
import { InventoryImportStatusDialog } from './components/InventoryImportStatusDialog/InventoryImportStatusDialog';

interface IImportInventory {
  open: boolean;
  onClose: () => void;
  docType: 'SpreadSheet' | 'Invoice';
}

interface IFormData {
  file?: FileList;
}

interface IError {
  response: {
    data: {
      message: string;
    };
  };
}

export const ImportInventory = ({
  open,
  onClose,
  docType,
}: IImportInventory) => {
  const portalDiv = document.getElementById('portal');

  const [topic, setTopic] = useState<'user' | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [labelText, setLabelText] = useState<string>('Importing');
  const [descriptionText, setDescriptionText] = useState<string>(
    'Keep this window open to see the results.',
  );
  const [showImportSuccessStatus, setShowImportSuccessStatus] =
    useState<boolean>(false);
  const [errorFile, setErrorFile] = useState<string>('');

  const [progress, setProgress] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [showTemplateDialog, setShowTemplateDialog] = useState<boolean>(false);

  const { seconds, minutes, start, pause } = useStopwatch({ autoStart: false });

  const schema = yup
    .object({
      file: yup.mixed().required(),
    })
    .required();

  const mutationFileUpload = useMutation(inventoryImport, {
    onSuccess: () => {
      setLabelText('Success');
      setShowImportSuccessStatus(true);
      setDisabled(false);
      pause();
    },
    onError: ({ response }: IError) => {
      setLabelText('Error');
      setDescriptionText(response.data.message);
      pause();
      setDisabled(false);
    },
  });

  const { register, handleSubmit } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const done = () => {
    onClose();
  };

  const onSubmit = (data: IFormData) => {
    if (!mutationFileUpload.isLoading && data.file) {
      mutationFileUpload.mutate({
        file: data.file?.[0],
        docType,
      });
    }
    setProgress(true);
    start();
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              {topic && (
                <>
                  <Back
                    src={BackIconImage}
                    alt="back"
                    onClick={() => setTopic(null)}
                  />
                  <SpacingDivider />
                </>
              )}
              <Title>Import {docType}</Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>

            {progress ? (
              <ProgressContainer>
                <Label status={labelText}>{labelText}</Label>

                <Divider height="8px" />

                <Row className="progressRow">
                  <HolderDiv>
                    <FileImage src={FileIcon} alt="FileIcon" />
                    <FileNameText className="file-name" limitOfLines={1}>
                      {fileName}
                    </FileNameText>
                  </HolderDiv>
                  <TimerText>{`${minutes < 10 ? `0${minutes}` : minutes}:${
                    seconds < 10 ? `0${seconds}` : seconds
                  }`}</TimerText>
                </Row>

                <Divider height="24px" />

                <DescriptionText>{descriptionText}</DescriptionText>

                <Line>
                  <Confirm
                    disabled={disabled || labelText === 'Error'}
                    onClick={done}
                  >
                    Done
                  </Confirm>
                </Line>
              </ProgressContainer>
            ) : (
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Label>File</Label>
                <Row className="pictureInputsWithLegend">
                  <Input
                    type="text"
                    placeholder={
                      docType === 'SpreadSheet'
                        ? 'Select a .csv or .xlsx file'
                        : 'Select a .pdf or .zip file'
                    }
                    value={fileName}
                    readOnly
                    isError={!!errorFile}
                  />

                  <SelectFile htmlFor="upload" isError={!!errorFile}>
                    Select File
                  </SelectFile>
                  <input
                    type="file"
                    id="upload"
                    accept={
                      docType === 'SpreadSheet' ? '.csv, .xlsx' : '.pdf, .zip'
                    }
                    hidden
                    {...register('file')}
                    onChange={e => {
                      const newFile = e.target.files?.[0];
                      if (newFile) {
                        register('file').onChange(e);
                        setFileName(e.target.files?.[0]?.name || '');

                        const allowedList =
                          docType === 'SpreadSheet'
                            ? [
                                'text/csv',
                                'application/vnd.ms-excel',
                                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                              ]
                            : [
                                'application/pdf',
                                'application/x-zip-compressed',
                                'application/zip',
                              ];

                        if (!allowedList.includes(newFile.type)) {
                          setErrorFile('Please select a .csv or .xlsx file.');
                        } else if (newFile.size >= 5242880) {
                          setErrorFile('Please select a file under 5MB.');
                        } else {
                          setErrorFile('');
                        }
                      }
                    }}
                  />
                </Row>

                {!!errorFile && (
                  <DescriptionText status="Error">{errorFile}</DescriptionText>
                )}

                {docType === 'SpreadSheet' && !errorFile && (
                  <TemplateDownloadSection>
                    Unsure where to start?{' '}
                    <DownloadLink
                      onClick={() => {
                        setShowTemplateDialog(true);
                      }}
                      type="button"
                    >
                      Download our template
                    </DownloadLink>
                  </TemplateDownloadSection>
                )}

                <Line>
                  <Confirm type="submit" disabled={!fileName || !!errorFile}>
                    Import
                  </Confirm>
                </Line>
              </Form>
            )}
            <InventoryImportStatusDialog
              open={showImportSuccessStatus}
              onClose={onClose}
            />

            <TemplateDownloadDialog
              open={showTemplateDialog}
              onClose={() => setShowTemplateDialog(false)}
            />
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
